import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { FaImage, FaRulerCombined, FaMapMarkerAlt, FaPalette, FaSync, FaSave } from 'react-icons/fa';
import './ImageEditorModal.css';

const ImageEditorModal = ({ item, sectionId, onClose, onUpdate }) => {
  const [settings, setSettings] = useState({
    objectFit: item.objectFit || 'cover',
    zoom: item.zoom || 100,
    offsetX: item.offsetX || 0,
    offsetY: item.offsetY || 0,
    brightness: item.brightness || 100,
    contrast: item.contrast || 100,
    blur: item.blur || 0,
    saturation: item.saturation || 100,
    grayscale: item.grayscale || 0,
    sepia: item.sepia || 0,
    hueRotate: item.hueRotate || 0,
    rotation: item.rotation || 0,
  });

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    onUpdate(sectionId, item.id, settings);
    onClose();
  };

  const handleReset = () => {
    const defaults = {
      objectFit: 'cover',
      zoom: 100,
      offsetX: 0,
      offsetY: 0,
      brightness: 100,
      contrast: 100,
      blur: 0,
      saturation: 100,
      grayscale: 0,
      sepia: 0,
      hueRotate: 0,
      rotation: 0,
    };
    setSettings(defaults);
  };

  const getImageStyle = () => {
    const transforms = [];
    if (settings.zoom !== 100) transforms.push(`scale(${settings.zoom / 100})`);
    if (settings.rotation !== 0) transforms.push(`rotate(${settings.rotation}deg)`);

    return {
      objectFit: settings.objectFit,
      objectPosition: `${50 + settings.offsetX}% ${50 + settings.offsetY}%`,
      transform: transforms.join(' '),
      filter: `brightness(${settings.brightness}%) contrast(${settings.contrast}%) blur(${settings.blur}px) saturate(${settings.saturation}%) grayscale(${settings.grayscale}%) sepia(${settings.sepia}%) hue-rotate(${settings.hueRotate}deg)`,
    };
  };

  return ReactDOM.createPortal(
    <div className="image-editor-modal-overlay" onClick={onClose}>
      <div className="image-editor-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3><FaImage /> ปรับแต่งภาพขั้นสูง - Slot {item.slotId}</h3>
          <button className="btn-close" onClick={onClose}>✕</button>
        </div>

        <div className="modal-content">
          <div className="preview-section">
            <div className="preview-container">
              <img 
                src={item.imageUrl} 
                alt={`Preview ${item.slotId}`}
                style={getImageStyle()}
              />
            </div>
            <div className="preview-label">ตัวอย่าง</div>
          </div>

          <div className="controls-section">
            <div className="control-group">
              <h4><FaRulerCombined /> การแสดงผล</h4>
              
              <div className="control-item">
                <label>Object Fit</label>
                <select
                  value={settings.objectFit}
                  onChange={(e) => handleChange('objectFit', e.target.value)}
                >
                  <option value="cover">Cover (เต็มกรอบ)</option>
                  <option value="contain">Contain (ครบทั้งภาพ)</option>
                  <option value="fill">Fill (ยืดเต็ม)</option>
                  <option value="none">None (ขนาดจริง)</option>
                </select>
              </div>

              <div className="control-item">
                <label>Zoom: {settings.zoom}%</label>
                <input
                  type="range"
                  min="50"
                  max="200"
                  value={settings.zoom}
                  onChange={(e) => handleChange('zoom', parseInt(e.target.value))}
                />
              </div>

              <div className="control-item">
                <label>Rotation: {settings.rotation}°</label>
                <input
                  type="range"
                  min="0"
                  max="360"
                  value={settings.rotation}
                  onChange={(e) => handleChange('rotation', parseInt(e.target.value))}
                />
              </div>
            </div>

            <div className="control-group">
              <h4><FaMapMarkerAlt /> ตำแหน่ง</h4>
              
              <div className="control-item">
                <label>Position X: {settings.offsetX}%</label>
                <input
                  type="range"
                  min="-100"
                  max="100"
                  value={settings.offsetX}
                  onChange={(e) => handleChange('offsetX', parseInt(e.target.value))}
                />
              </div>

              <div className="control-item">
                <label>Position Y: {settings.offsetY}%</label>
                <input
                  type="range"
                  min="-100"
                  max="100"
                  value={settings.offsetY}
                  onChange={(e) => handleChange('offsetY', parseInt(e.target.value))}
                />
              </div>
            </div>

            <div className="control-group">
              <h4><FaPalette /> ฟิลเตอร์</h4>
              
              <div className="control-item">
                <label>Brightness: {settings.brightness}%</label>
                <input
                  type="range"
                  min="0"
                  max="200"
                  value={settings.brightness}
                  onChange={(e) => handleChange('brightness', parseInt(e.target.value))}
                />
              </div>

              <div className="control-item">
                <label>Contrast: {settings.contrast}%</label>
                <input
                  type="range"
                  min="0"
                  max="200"
                  value={settings.contrast}
                  onChange={(e) => handleChange('contrast', parseInt(e.target.value))}
                />
              </div>

              <div className="control-item">
                <label>Saturation: {settings.saturation}%</label>
                <input
                  type="range"
                  min="0"
                  max="200"
                  value={settings.saturation}
                  onChange={(e) => handleChange('saturation', parseInt(e.target.value))}
                />
              </div>

              <div className="control-item">
                <label>Blur: {settings.blur}px</label>
                <input
                  type="range"
                  min="0"
                  max="10"
                  value={settings.blur}
                  onChange={(e) => handleChange('blur', parseInt(e.target.value))}
                />
              </div>

              <div className="control-item">
                <label>Grayscale: {settings.grayscale}%</label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={settings.grayscale}
                  onChange={(e) => handleChange('grayscale', parseInt(e.target.value))}
                />
              </div>

              <div className="control-item">
                <label>Sepia (ย้อนยุค): {settings.sepia}%</label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={settings.sepia}
                  onChange={(e) => handleChange('sepia', parseInt(e.target.value))}
                />
              </div>

              <div className="control-item">
                <label>Hue Rotate: {settings.hueRotate}°</label>
                <input
                  type="range"
                  min="0"
                  max="360"
                  value={settings.hueRotate}
                  onChange={(e) => handleChange('hueRotate', parseInt(e.target.value))}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn-reset" onClick={handleReset}>
            <FaSync /> รีเซ็ต
          </button>
          <div className="footer-actions">
            <button className="btn-cancel" onClick={onClose}>
              ยกเลิก
            </button>
            <button className="btn-save" onClick={handleSave}>
              <FaSave /> บันทึก
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ImageEditorModal;
